import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import SplitterPaneDividerViewModel from '@/ViewModels/Controls/SplitterPaneDividerViewModel';

export default class SplitterPaneDivider extends Control {
    private jQueryControl: JQuery;

    constructor(private viewModel: SplitterPaneDividerViewModel) {
        super(viewModel);
    }

    public getControl(): JQuery {
        if (this.jQueryControl)
            return this.jQueryControl;

        this.jQueryControl = $('<div>');

        this.applyBindings(this.jQueryControl, {
            controlName: 'splitterpanedivider',
            locator: this.viewModel.locator,
            controlIdent: this.identifier,
            visible: this.viewModel.isVisible,
            states: this.definePureComputed({
                read: () => {
                    const states = [this.viewModel.isEnabled() ? 'enabled' : 'disabled'];
                    if (this.viewModel.isClickable())
                        states.push('clickable');
                    if (this.viewModel.isMovable())
                        states.push('movable');
                    if (this.viewModel.isCollapsed())
                        states.push('collapsed');
                    return states;
                }
            }),
        });

        this.setupClick();
        this.setupResizing();

        return this.jQueryControl;
    }

    private setupClick() {
        this.jQueryControl.on('mousedown', (downEvent: JQuery.MouseDownEvent) => {
            if (!this.viewModel.isClickable())
                return;

            // Nur Linksklick
            if (downEvent.button !== 0)
                return;

            const mouseUpHandler = (upEvent: JQuery.MouseUpEvent) => {
                this.jQueryControl.off('mouseup', mouseUpHandler);
                if (downEvent.pageX !== upEvent.pageX || downEvent.pageY !== upEvent.pageY) {
                    return;
                }
                this.viewModel.onClick();
            };
            this.jQueryControl.on('mouseup', mouseUpHandler);
        });
    }

    private setupResizing() {
        this.jQueryControl.on('mousedown', (downEvent: JQuery.MouseDownEvent) => {
            if (!this.viewModel.isMovable())
                return;

            // Nur Linksklick
            if (downEvent.button !== 0)
                return;

            const startingPosition = {
                x: downEvent.pageX,
                y: downEvent.pageY,
            };
            const mouseOffsetToDivider = {
                x: startingPosition.x - $(downEvent.currentTarget).offset().left,
                y: startingPosition.y - $(downEvent.currentTarget).offset().top,
            };
            const horizontal = this.viewModel.attachedTo.isHorizontal();
            const clazz = `splitter-resizing splitter-resizing-${horizontal ? 'horizontal' : 'vertical'}`;
            const calculatedSize = this.viewModel.attachedTo.calculatedSize();

            const positionRelativeToParent = horizontal ? this.jQueryControl.position().left : this.jQueryControl.position().top;
            const $ghostDivider = this.jQueryControl.clone();
            $ghostDivider.hide();
            $ghostDivider.addClass('ghost-divider');
            $ghostDivider.insertAfter(this.jQueryControl);
            const dividerSize = horizontal ? $ghostDivider.width() : $ghostDivider.height();

            let moved = false;
            const mouseMoveHandler = (moveEvent: MouseEvent) => {
                if (!moved) {
                    moved = true;
                    $ghostDivider.show();
                    this.viewModel.triggerResizeStart();
                }

                $('body').addClass(clazz);
                const offset = horizontal
                    ? (startingPosition.x - mouseOffsetToDivider.x) - moveEvent.pageX
                    : (startingPosition.y - mouseOffsetToDivider.y) - moveEvent.pageY;

                $ghostDivider.css(horizontal ? 'left' : 'top', positionRelativeToParent - (offset + dividerSize / 2));
            };

            const mouseUpHandler = (upEvent: MouseEvent) => {
                window.removeEventListener('mousemove', mouseMoveHandler);
                window.removeEventListener('mouseup', mouseUpHandler);

                const offset = horizontal
                    ? (startingPosition.x - mouseOffsetToDivider.x) - upEvent.pageX
                    : (startingPosition.y - mouseOffsetToDivider.y) - upEvent.pageY;
                this.viewModel.attachedTo.size(calculatedSize + offset + dividerSize / 2);
                this.viewModel.triggerResizeEnd();

                $('body').removeClass(clazz);
                $ghostDivider.remove();
            };

            window.addEventListener('mousemove', mouseMoveHandler);
            window.addEventListener('mouseup', mouseUpHandler);
        });
    }

    protected disposeInternal() {
        this.jQueryControl.off('mousedown');
        this.jQueryControl.off('mouseup');
        super.disposeInternal();
    }
}

ComponentManager.setControl('SplitterPaneDivider', SplitterPaneDivider, 'RADPresentation.HTMLClient.DefaultViewExtensions');